import { createSelector } from "reselect"
import moment from "moment"
import { get, map, chain, sumBy, last, reduce, take, slice, find, maxBy } from "lodash"
import { getSelectedDate } from "modules/security-profile/selectors/dates"
import { getModelFactors } from "selectors/modelMetadata"

const getSecurityPerformanceHistory = state => get(state, "global.data.securityPerformanceHistory.data.model.simulation.performance")
const getCategory = state => get(state, "modules.securityProfilePerformance.displayType")

function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  return factor.overviewLabel || factor.label
}

function getFactorCategory(factors, id) {
  return get(factors, [id, "category"])
}

function getTakePlus(count, combine, accumulator) {
  return (values) => {
    if (values.length <= 6) {
      return values
    } else {
      const reduced = reduce(slice(values, 5), combine, accumulator)
      return [...take(values, 5), reduced]
    }
  }
}

export const getPerformanceHistory = createSelector(
  [getSecurityPerformanceHistory],
  (performance) => {
    return map(performance, daily => ({
      date: daily.date,
      total: get(daily, "totalReturn.cumulativePercent"),
      factorsTotal: get(daily, "attribution.summary.factors.cumulativePercent"),
      alpha: get(daily, "attribution.summary.specific.cumulativePercent"),
      trading: get(daily, "attribution.summary.trading.cumulativePercent"),
      factors: chain(daily.attribution.factors)
        .keyBy("id")
        .mapValues(factor => get(factor, "return.cumulativePercent"))
        .value(),
      categories: chain(daily.attribution.factors)
        .groupBy("category")
        .mapValues(factors => sumBy(factors, "return.cumulativePercent"))
        .value(),
    }))
  },
)

export const getCurrentPerformance = createSelector(
  [getPerformanceHistory],
  (performanceHistory) => last(performanceHistory),
)

const getSelectedDateFactors = createSelector(
  [getSecurityPerformanceHistory, getSelectedDate],
  (securityPerformanceHistory, date) => {
    const history = get(find(securityPerformanceHistory, d => moment(d.date).isSame(date)), "attribution.factors")

    return chain(history)
      .keyBy("id")
      .mapValues(item => ({ value: item.return.cumulativePercent }))
      .value()
  },
)

export const getPerformanceAttributionData = createSelector(
  [getSelectedDateFactors, getCategory, getModelFactors],
  (selectedDateFactors, category, factorsMetadata) => {
    const factors = chain(selectedDateFactors)
      .map((factor, id) => ({
        id,
        value: factor.value,
        label: getFactorLabel(factorsMetadata, id),
        category: getFactorCategory(factorsMetadata, id),
      }))
      .filter(factor =>
        !category || category === "total" || category === "factors" || category === factor.category,
      )
      .reduce((accumulator, factor) => {
        if (factor.value > 0) {
          accumulator.positive.push(factor)
        } else if (factor.value < 0) {
          accumulator.negative.push(factor)
        }

        return accumulator
      }, { positive: [], negative: [] })
      .value()

    const totalPositive = sumBy(factors.positive, factor => factor.value)
    const totalNegative = sumBy(factors.negative, factor => factor.value)

    const topFactors = chain(factors)
      .mapValues(factors =>
        chain(factors)
          .sortBy(factor => -Math.abs(factor.value))
          .thru(getTakePlus(5, (accumulator, factor) => {
            return { id: "other", value: accumulator.value + factor.value }
          }, { id: "other", value: 0 }))
          .value(),
      )
      .value()

    return {
      totalPositive,
      totalNegative,
      factors: topFactors,
    }
  },
)

export const getMaxFactors = createSelector(
  [getPerformanceAttributionData],
  (performanceAttribution) => {
    if (!performanceAttribution) {
      return 0
    }

    const factors = [
      ...performanceAttribution.factors.positive,
      ...performanceAttribution.factors.negative,
    ]

    if (factors.length > 0) {
      return Math.abs(maxBy(factors, item => Math.abs(item.value)).value)
    } else {
      return 0
    }
  },
)
